import { Icon } from '@natera/material/lib/icon'
import { Dialog } from 'radix-ui'
import { useMemo } from 'react'
import { Document, Page } from 'react-pdf'
import { cn } from '~/shared/lib/cn'
import { useZoomedPageResizableContainer } from '../../model'

interface PageDialogProps {
  pdfData: ArrayBuffer | null
  pageNumber: number | null
  onClose: () => void
}

export function PageDialog({ pdfData, pageNumber, onClose }: PageDialogProps) {
  const {
    containerRef,
    containerHeight: availableHeight,
    containerWidth: availableWidth,
    initPagesOnDocumentLoad,
    isDocumentLoaded,
  } = useZoomedPageResizableContainer()

  // Only pass one of width or height to <Page />
  const pageProps: { width?: number; height?: number } = useMemo(() => {
    if (availableHeight && availableWidth) {
      // Use the smaller dimension to avoid overflow
      if (availableWidth / 1.2925 < availableHeight) {
        return { width: availableWidth }
      } else {
        return { height: availableHeight }
      }
    } else if (availableWidth) {
      return { width: availableWidth }
    } else if (availableHeight) {
      return { height: availableHeight }
    }
    return {}
  }, [availableHeight, availableWidth])

  if (pageNumber === null) {
    return null
  }

  return (
    <Dialog.Root
      open
      onOpenChange={(open) => {
        if (!open) {
          onClose()
        }
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay className="data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-50 bg-black/50" />
        <Dialog.Content
          ref={containerRef}
          className={cn(
            'bg-surface-lowest data-[state=open]:animate-in data-[state=open]:zoom-in data-[state=closed]:animate-out data-[state=closed]:zoom-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 fixed top-[50%] left-[50%] z-50 flex h-[calc(100dvh-88px)] w-[calc((100dvh-88px)/1.2925)] max-w-[calc(100dvw-88px)] translate-x-[-50%] translate-y-[-50%] items-center justify-center duration-200',
          )}
        >
          <Dialog.Title className="hidden">Page {pageNumber}</Dialog.Title>
          <Dialog.Description className="hidden" />
          <Document
            className="w-fit"
            file={pdfData}
            onLoadSuccess={initPagesOnDocumentLoad}
            loading=""
            noData=""
          >
            {isDocumentLoaded && pageNumber !== null && (
              <Page
                pageNumber={pageNumber}
                renderAnnotationLayer={false}
                renderTextLayer={false}
                {...pageProps}
              />
            )}
          </Document>
        </Dialog.Content>
        <Dialog.Close className="pointer-events-auto fixed top-6 right-6 z-50 flex size-8 cursor-pointer items-center justify-center border-none bg-transparent p-0">
          <Icon
            className="text-content-highest-emphasis-inverse"
            children="close"
          />
        </Dialog.Close>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
